
import Vue from "vue";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import { configStore, uiStore } from "@/store/store";
import {
  ExternalImage,
  ExternalImageV,
  Image,
  ImageV,
  MediaSizeOptions,
  Size
} from "@/types/media";
import lodash from "lodash";
import defensiveCopy from "@/util/defensive-copy";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import { Control, ControlsTreeContext } from "@/types/controls";
import MediaCropper from "@/components/Reusable/CustomInputs/MediaSize/MediaCropper.vue";
import { CUSTOM_SIZE_KEY, fetchMediaSizes } from "@/lib/media/media";
import * as t from "io-ts";
import { isDynamicShortcode } from "@/lib/dynamic-data/utils";

type Srcset = {
  srcset: string;
  sizes: string;
};

@Component({
  components: { MediaCropper, DropdownInput }
})
export default class MediaSizeInput extends Vue {
  @Inject("context") readonly context!: ControlsTreeContext;

  @Prop()
  value?: string;

  @Prop()
  control!: Control;

  @Prop()
  options?: MediaSizeOptions;

  @Prop()
  path!: string;

  sizes: Record<string, Srcset> = {};

  @Watch("value")
  onSizeChange() {
    this.setSrcsetBySize(this.value || "full");
  }

  @Watch("image", { immediate: true })
  async onImageChanged(
    newImage?: Image | ExternalImage,
    oldImage?: Image | ExternalImage
  ) {
    if (!this.image) {
      this.sizes = {};
      return;
    }

    if (newImage?.id !== oldImage?.id && newImage?.type === "image") {
      this.sizes = await fetchMediaSizes(this.image.id);
    }
  }

  get noDataAvailableMessage() {
    if (this.image && this.image.type === "external_image") {
      return "Not available for external images";
    }

    return "No image chosen";
  }

  get availableSizes() {
    return configStore.availableMediaSizes.map(size => {
      return {
        text: size.label,
        subLabel: size.subLabel,
        value: size.slug
      };
    });
  }

  get filteredItems() {
    const items = this.availableSizes.filter(item => {
      if (!this.imageSizes.length) return true;
      // Only show sizes that are available for the associated image.
      return this.imageSizes.includes(item.value);
    });

    if (this.isCropAllowed) {
      items.push({
        text: "Custom...",
        value: CUSTOM_SIZE_KEY,
        subLabel: undefined
      });
    }

    return items;
  }

  get imageSizes() {
    return Object.keys(this.sizes || {});
  }

  get disabled() {
    if (isDynamicShortcode(this._imageControlValue)) return false;
    return this.image?.type === "external_image";
  }

  get isCropAllowed() {
    return !!this.imagePath && !isDynamicShortcode(this._imageControlValue);
  }

  get hasBreakpoints() {
    return !!this.context.getControlValue(this.imagePathWithBreakpoint);
  }

  get _imageControlValue() {
    if (!this.imagePath) return null;

    return (
      this.context.getControlValue(this.imagePathWithBreakpoint) ||
      this.context.getControlValue(this.imagePath)
    );
  }

  get image(): Image | ExternalImage | null {
    const image = this._imageControlValue;
    const decoder = t.union([ImageV, ExternalImageV]).decode;

    return pipe(
      image,
      decoder,
      fold(
        () => null,
        validImage => validImage
      )
    );
  }

  get imagePath() {
    if (!this.options?.imagePropertyPath) {
      return undefined;
    }

    // An "imagePropertyPath" can be "content.content.slider[].image".
    // We turn it into a full path with the repeater's index.
    // We're making the assumption that the path points to an image control inside the same repeater item.
    const matchForRepeaterIndex = this.path.match(/\[(.*?)\]/);

    if (matchForRepeaterIndex) {
      const repeaterIndex = matchForRepeaterIndex[1];

      return this.options.imagePropertyPath.replace("[]", `[${repeaterIndex}]`);
    }

    return this.options.imagePropertyPath;
  }

  get imagePathWithBreakpoint() {
    return `${this.imagePath}.${uiStore.currentBreakpoint.id}`;
  }

  setCustomSize(size?: Size) {
    if (!size) return;
    this.updateImage(`sizes.${CUSTOM_SIZE_KEY}`, size);
  }

  setImageAttribute(name: string, value: string) {
    const path = `attributes.${name}`;
    this.updateImage(path, value);
  }

  updateImage(path: string, value: unknown) {
    const newImage = defensiveCopy(this.image);
    if (!this.imagePath) return; // No image associated with this input
    if (!newImage) return; // Image is empty

    lodash.set(newImage, path, value);

    const imagePath = this.hasBreakpoints
      ? this.imagePathWithBreakpoint
      : this.imagePath;

    this.context.setControlValue(imagePath, newImage, this.control);
  }

  async setSrcsetBySize(size = "full") {
    if (!this.image) return;
    if (this.options?.disableSrcset) return;

    if (!this.sizes[size] || size === CUSTOM_SIZE_KEY) {
      this.setImageAttribute("srcset", "");
      this.setImageAttribute("sizes", "");
      return;
    }

    const { srcset, sizes } = this.sizes[size];
    this.setImageAttribute("srcset", srcset);
    this.setImageAttribute("sizes", sizes);
  }

  emit(size?: string) {
    this.$emit("input", size || "");
  }
}
